import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Calendar, Link2, User } from 'lucide-react';
import { API, errorText } from '../governance_v2/constants';
import { ACTIVITY_STATUS, fmtDate, memberName } from './constants';

const ActivityCard = ({ a, members, activities, canDrag, onOpen }) => (
  <div draggable={canDrag} onDragStart={(e) => e.dataTransfer.setData('text/plain', a.id)} onClick={() => onOpen(a)}
    className={`group cursor-pointer rounded-lg border border-slate-200 bg-white p-3 shadow-sm transition-[transform,box-shadow] duration-150 hover:-translate-y-0.5 hover:shadow-md dark:border-slate-700 dark:bg-slate-900 ${canDrag ? 'active:cursor-grabbing' : ''}`}
    data-testid={`kanban-card-${a.id}`}>
    <p className="text-sm font-medium text-slate-900 dark:text-white">{a.title}</p>
    <div className="mt-2 h-1.5 rounded bg-slate-200 dark:bg-slate-700"><div className={`h-1.5 rounded ${ACTIVITY_STATUS[a.status].bar}`} style={{ width: `${a.progress}%` }} /></div>
    <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-slate-500">
      <span className="inline-flex items-center gap-1"><User className="h-3 w-3" />{memberName(members, a.assignee_user_id)}</span>
      <span className="inline-flex items-center gap-1"><Calendar className="h-3 w-3" />{fmtDate(a.end_date)}</span>
      <span>{a.progress}%</span>
      {a.depends_on?.length > 0 && <span className="inline-flex items-center gap-1" title={a.depends_on.map((d) => activities.find((x) => x.id === d)?.title).join(', ')}><Link2 className="h-3 w-3" />{a.depends_on.length}</span>}
    </div>
  </div>
);

export const KanbanBoard = ({ companyId, projectId, activities, members, canManage, currentUserId, onOpen }) => {
  const qc = useQueryClient();
  const [over, setOver] = useState(null);
  const move = useMutation({
    mutationFn: ({ id, status }) => axios.patch(`${API}/companies/${companyId}/activities/${id}`, { status }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['project', companyId, projectId] }); qc.invalidateQueries({ queryKey: ['projects', companyId] }); qc.invalidateQueries({ queryKey: ['portfolio', companyId] }); },
    onError: (e) => toast.error(errorText(e)),
  });
  const canDrag = (a) => canManage || a.assignee_user_id === currentUserId;
  const drop = (status) => (e) => {
    e.preventDefault(); setOver(null);
    const a = activities.find((x) => x.id === e.dataTransfer.getData('text/plain'));
    if (a && a.status !== status && canDrag(a)) move.mutate({ id: a.id, status });
  };
  return (
    <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4" data-testid="kanban-board">
      {Object.entries(ACTIVITY_STATUS).map(([status, meta]) => {
        const col = activities.filter((a) => a.status === status);
        return (
          <div key={status} onDragOver={(e) => { e.preventDefault(); setOver(status); }} onDragLeave={() => setOver(null)} onDrop={drop(status)}
            className={`min-h-[220px] rounded-xl border p-3 transition-colors duration-150 ${over === status ? 'border-sky-400 bg-sky-50 dark:bg-sky-950/30' : 'border-slate-200 bg-slate-50/60 dark:border-slate-800 dark:bg-slate-900/40'}`}
            data-testid={`kanban-column-${status}`}>
            <p className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-500">
              <span className={`h-2 w-2 rounded-full ${meta.dot}`} />{meta.label}<span className="ml-auto rounded bg-slate-200 px-1.5 text-slate-600 dark:bg-slate-800 dark:text-slate-400">{col.length}</span>
            </p>
            <div className="space-y-2">{col.map((a) => <ActivityCard key={a.id} a={a} members={members} activities={activities} canDrag={canDrag(a)} onOpen={onOpen} />)}</div>
          </div>
        );
      })}
    </div>
  );
};
